import type { MaybeRefOrGetter } from "vue";
import TermsService from "@/api/models/terms/terms";
import type {
  TTermsDocument,
  TTermsDocumentFilters,
  TTermsDocumentVersion,
  TTermsDocumentVersionListItem,
  TTermsDocumentVersionCreate,
  TTermsDocumentVersionUpdate,
  TTermsDocumentVersionAcceptResponse,
  TTermsDocumentVersionHistoryEntry,
  ITermsDocumentVersionFilter,
  TVersionItemPathParams,
} from "@/api/models/terms/terms";
import {
  useGenericDetailQuery,
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import { createQueryKeys } from "@/composables/shared/queries/useQueryKeyBuilder";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation.ts";

type TVersionItemParams = { doc_pk: string; id: number };

const TermsQueryKeys = createQueryKeys(TermsService.queryKey, (key) => ({
  documents: key<TTermsDocumentFilters>("documents"),
  document: key("document"),
  versions: key<ITermsDocumentVersionFilter>("versions"),
  versionsPaginated: key<ITermsDocumentVersionFilter>("versionsPaginated"),
  versionHistory: key<TVersionItemParams>("versionHistory"),
  version: key<TVersionItemPathParams>("version"),
}));

// ---------- Documents ----------

export const useTermsDocument = (
  id: MaybeRefOrGetter<string | null | undefined>,
  options: TItemQueryBehaviorOptions = {}
) =>
  useGenericQuery<TTermsDocument>({
    queryKey: TermsQueryKeys.document(),
    params: { id },
    fetcher: (id) => TermsService.get({ id }),
    // Readable while the acceptance dialog blocks the rest of the app.
    ignorePendingTermsBlock: true,
    ...options,
  });

export const useTermsDocuments = (
  filters: MaybeRefOrGetter<Partial<TTermsDocumentFilters>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTermsDocument>({
    params: { filters, enabled },
    queryKey: TermsQueryKeys.documents(),
    fetcher: () => TermsService.getAll(),
    ignorePendingTermsBlock: true,
  });

export function useCreateTermsDocument() {
  return useGenericMutation<TTermsDocument, { title: string }>({
    mutationFn: (variables) => TermsService.create({}, variables),
    feature: "terms-document-create",
    invalidates: [TermsQueryKeys.documents()],
  });
}

export function useUpdateTermsDocument() {
  return useGenericMutation<TTermsDocument, { id: string; payload: { title: string } }>({
    mutationFn: (variables) => TermsService.update({ id: variables.id }, variables.payload),
    feature: "terms-document-update",
    invalidates: [TermsQueryKeys.documents()],
  });
}

export function useRemoveTermsDocument() {
  return useGenericMutation<void, string>({
    mutationFn: (id) => TermsService.remove({ id }),
    feature: "terms-document-remove",
    invalidates: [TermsQueryKeys.documents()],
  });
}

// ---------- Versions ----------

export const useTermsDocumentVersions = (
  filters?: MaybeRefOrGetter<Partial<ITermsDocumentVersionFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTermsDocumentVersionListItem, Partial<ITermsDocumentVersionFilter>>({
    queryKey: TermsQueryKeys.versions(),
    params: { filters, enabled },
    fetcher: (filters) => TermsService.versions.getAll({ doc_pk: filters?.doc_pk! }),
    neededKey: ["doc_pk"],
    // The pending-terms dialog reads version history while the block is active.
    ignorePendingTermsBlock: true,
  });

export const useTermsDocumentVersionsPaginated = (
  params: TOwnedFilterParams<ITermsDocumentVersionFilter> = {}
) =>
  useGenericPaginatedQuery<TTermsDocumentVersionListItem, ITermsDocumentVersionFilter>({
    queryKey: TermsQueryKeys.versionsPaginated(),
    params,
    fetcher: (fetchParams) =>
      TermsService.versions.getPaginatedFiltered(
        { doc_pk: fetchParams.filters.doc_pk! },
        fetchParams.filters,
        fetchParams.pageParam
      ),
    neededKey: ["doc_pk"],
  });

export function useCreateTermsDocumentVersion() {
  return useGenericMutation<
    TTermsDocumentVersionListItem,
    { docId: string; payload: TTermsDocumentVersionCreate }
  >({
    mutationFn: (variables) =>
      TermsService.versions.create({ doc_pk: variables.docId }, variables.payload),
    feature: "terms-document-version-create",
    invalidates: [TermsQueryKeys.versions(), TermsQueryKeys.documents()],
  });
}

export function useUpdateTermsDocumentVersion() {
  return useGenericMutation<
    TTermsDocumentVersionListItem,
    { docId: string; version: number; payload: TTermsDocumentVersionUpdate }
  >({
    mutationFn: (variables) =>
      TermsService.versions.update(
        { doc_pk: variables.docId, id: variables.version },
        variables.payload
      ),
    feature: "terms-document-version-update",
    invalidates: [
      TermsQueryKeys.versions(),
      TermsQueryKeys.documents(),
      TermsQueryKeys.versionsPaginated(),
    ],
    sets: (_data, vars) => [TermsQueryKeys.version({ doc_pk: vars.docId, id: vars.version })],
  });
}

export function useDeleteTermsDocumentVersion() {
  return useGenericMutation<void, { docId: string; version: number }>({
    mutationFn: (variables) =>
      TermsService.versions.remove({ doc_pk: variables.docId, id: variables.version }),
    feature: "terms-document-version-delete",
    invalidates: [
      TermsQueryKeys.versions(),
      TermsQueryKeys.documents(),
      TermsQueryKeys.versionsPaginated(),
    ],
  });
}

export function useAcceptTermsDocumentVersion() {
  return useGenericMutation<
    TTermsDocumentVersionAcceptResponse,
    { docId: string; version: number }
  >({
    mutationFn: (variables) =>
      TermsService.versions.accept({ doc_pk: variables.docId, id: variables.version }, {}),
    feature: "terms-document-version-accept",
    invalidates: [TermsQueryKeys.documents()],
  });
}

export const useTermsDocumentVersionHistory = (
  filters: MaybeRefOrGetter<Partial<TVersionItemParams>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TTermsDocumentVersionHistoryEntry, Partial<TVersionItemParams>>({
    queryKey: TermsQueryKeys.versionHistory(),
    params: { filters, enabled },
    fetcher: (f) => TermsService.versions.history({ doc_pk: f?.doc_pk!, id: f?.id! }),
    neededKey: ["doc_pk", "id"],
  });

export const useTermVersion = (
  filters: MaybeRefOrGetter<Partial<TVersionItemPathParams>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericDetailQuery<TTermsDocumentVersion, Partial<TVersionItemPathParams>>({
    queryKey: TermsQueryKeys.version(),
    params: { filters, enabled },
    neededKey: ["doc_pk", "id"],
    fetcher: (params) => TermsService.versions.getVersion(params as TVersionItemPathParams),
    ignorePendingTermsBlock: true,
  });
